import React, { useContext, useEffect } from "react";
import { useSnackbar } from "notistack";
import {
  Button,
  FormControl,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  Paper,
  TextField,
} from "@mui/material";

import { Cached, Search } from "@mui/icons-material";
import { IntervaloDataContext } from "../../Context/IntevervaloDataContext";
import {
  SincronizacaoFaturamentoContext,
  TIPOS_SINCRONIZACAO,
  obterMensagemErro,
} from "../../Context/SincronizacaoFaturamentoContext";
import { RecebimentoContext } from "../../Recebimento/Context/RecebimentoContext";

import MascaraDeCompetencia from "../MascaraDeCompetencia";
import debounce from "../../../../utils/debounce";

import GridContainer from "../../../../componentsv2/GridContainer";
import GridItem from "../../../../componentsv2/GridItem";

import "./styles.css";
import DataPickerAtualizado from "../../../../components/DataPickerAtualizado";
import AutocompleteMultiple from "../../../../componentsv2/AutocompleteMultiple";
import { solicitarAtualizacaoGeral } from "../../services/sincronizacaoFaturamento";
import FiltrosLotes from "../../Lotes/components/FiltrosLotes";
import {
  filtrarConvenios,
  obterLabelConvenio,
} from "../../convenio";

function obterPeriodoDaCompetencia(inicio, fim) {
  const competenciaInicial = String(inicio || "").match(/^(\d{2})\/(\d{4})$/);
  const competenciaFinal = String(fim || "").match(/^(\d{2})\/(\d{4})$/);

  if (!competenciaInicial || !competenciaFinal) {
    return null;
  }

  const mesInicial = Number(competenciaInicial[1]);
  const anoInicial = Number(competenciaInicial[2]);
  const mesFinal = Number(competenciaFinal[1]);
  const anoFinal = Number(competenciaFinal[2]);
  const chaveInicial = anoInicial * 100 + mesInicial;
  const chaveFinal = anoFinal * 100 + mesFinal;

  if (
    mesInicial < 1 ||
    mesInicial > 12 ||
    mesFinal < 1 ||
    mesFinal > 12 ||
    chaveInicial > chaveFinal
  ) {
    return null;
  }

  const diaFinal = new Date(anoFinal, mesFinal, 0).getDate();
  const formatarData = (ano, mes, dia, horario) =>
    `${ano}-${String(mes).padStart(2, "0")}-${String(dia).padStart(
      2,
      "0",
    )} ${horario}`;

  return {
    dataInicio: formatarData(anoInicial, mesInicial, 1, "00:00:00"),
    dataFim: formatarData(anoFinal, mesFinal, diaFinal, "23:59:59"),
  };
}

export default function Formulario(props) {
  const { enqueueSnackbar } = useSnackbar();

  const {
    dataInicio,
    setDataInicio,
    dataFim,
    setDataFim,
    setCompetencia,
    competenciaPadrao,
    competenciaInicial,
    setCompetenciaInicial,
    competenciaFinal,
    setCompetenciaFinal,
    notaFiscal,
    setNotaFiscal,
    competencia,
  } = useContext(IntervaloDataContext);

  const {
    aba,
    resultado,
    setDadosTotalizadores,
    setDadosTabela,
    atualizaFiltro,
    setAtualizaTabela,
    protocolo,
    setProtocolo,
    plano,
    setPlano,
    lote,
    setLote,
    listaConvenios,
    setAtualizaGeradoSoftlab,
    responsavelPlano,
    setResponsavelPlano,
    listaResponsaveisFiltrados,
  } = useContext(RecebimentoContext);

  const {
    atualizacaoGeralAtiva,
    registrarInicio,
    cancelarInicio,
    registrarResultado,
    registrarFalha,
  } = useContext(SincronizacaoFaturamentoContext);

  const {
    tabelaFaturamento,
    setDadosTabelaFaturamento,
    setAtualizaTabelaFaturamento,
    faturamento,
    visualizacao,
    filtroProtocolo,
    setFiltroProtocolo,
    filtroPlano,
    setFiltroPlano,
    filtroResponsavelPlano,
    setFiltroResponsavelPlano,
    filtroLote,
    setFiltroLote,
    filtroSituacao,
    setFiltroSituacao,
    carregandoTabela,
    recebimento,
    setOpenModalInformarFaturamento,
  } = props;

  const protocoloFiltroAtual = filtroProtocolo ?? protocolo;
  const setProtocoloFiltro = setFiltroProtocolo ?? setProtocolo;
  const planoFiltroAtual = filtroPlano ?? plano;
  const setPlanoFiltro = setFiltroPlano ?? setPlano;
  const responsavelPlanoFiltroAtual = filtroResponsavelPlano ?? responsavelPlano;
  const setResponsavelPlanoFiltro = setFiltroResponsavelPlano ?? setResponsavelPlano;
  const loteFiltroAtual = filtroLote ?? lote;
  const setLoteFiltro = setFiltroLote ?? setLote;
  const situacaoFiltroAtual = filtroSituacao ?? "";
  const setSituacaoFiltro = setFiltroSituacao ?? (() => {});

  // Filtros do formulário principal, com exceção da aba BI
  useEffect(() => {
    const dados = faturamento ? tabelaFaturamento : resultado;
    const normalizarTexto = (valor) => String(valor ?? "").trim().toUpperCase();
    const nomesPlanos = (Array.isArray(planoFiltroAtual) ? planoFiltroAtual : [])
      .map((element) => normalizarTexto(element?.convenio))
      .filter(Boolean);
    const nomesResponsaveis = (Array.isArray(responsavelPlanoFiltroAtual)
      ? responsavelPlanoFiltroAtual
      : []
    )
      .map((el) => normalizarTexto(el?.grupo))
      .filter(Boolean);
    const protocoloFiltro = normalizarTexto(protocoloFiltroAtual);
    const loteFiltro = normalizarTexto(loteFiltroAtual);
    const filtraPlano =
      nomesPlanos.length > 0 && !nomesPlanos.includes("TODOS");
    const filtraGrupo =
      nomesResponsaveis.length > 0 && !nomesResponsaveis.includes("TODOS");

    if (dados) {
      let filtro = dados.filter(
        (p) =>
          (!protocoloFiltro ||
            normalizarTexto(p?.protocolo).includes(protocoloFiltro)) &&
          (!filtraPlano || nomesPlanos.includes(normalizarTexto(p?.plano))) &&
          normalizarTexto(p?.lote).includes(loteFiltro) &&
          (!situacaoFiltroAtual ||
            normalizarTexto(p?.status || "ABERTO") ===
              normalizarTexto(situacaoFiltroAtual)) &&
          (!filtraGrupo ||
            nomesResponsaveis.includes(normalizarTexto(p?.grupo)))
      );
      if (recebimento) {
        setDadosTotalizadores(filtro);

        // Filtro referente aos somatórios da aba de recebimento
        if (aba !== "Enviado") {
          filtro = filtro.filter(
            (p) =>
              (aba === "Recebido" || aba === "Líquido"
                ? p.valor_recuperado
                : true) &&
              (aba === "Glosado" ? p.valor_glosado : true) &&
              (aba === "Dif. Tabela" ? p.valor_diferenca_tabela : true) &&
              // (aba === "Por Recurso" ? p.valor_recurso : true) &&
              (aba === "Em recurso" ? p.em_analise : true)
          );
        }

        setDadosTabela(filtro);
      } else {
        setDadosTabelaFaturamento(filtro);
      }
    }
  }, [
    planoFiltroAtual,
    protocoloFiltroAtual,
    loteFiltroAtual,
    situacaoFiltroAtual,
    setDadosTotalizadores,
    setDadosTabela,
    setDadosTabelaFaturamento,
    tabelaFaturamento,
    resultado,
    aba,
    faturamento,
    recebimento,
    atualizaFiltro,
    responsavelPlanoFiltroAtual,
  ]);

  async function sincronizaTabelasFaturamento() {
    const periodoCompetencia = obterPeriodoDaCompetencia(
      competenciaInicial,
      competenciaFinal,
    );

    if (!periodoCompetencia) {
      enqueueSnackbar(
        "Selecione uma competência inicial e final válidas para sincronizar.",
        { variant: "warning" },
      );
      return;
    }

    const { dataInicio: data_inicial, dataFim: data_final } =
      periodoCompetencia;
    const execucaoOtimista = registrarInicio({
      tipo: TIPOS_SINCRONIZACAO.ATUALIZACAO_GERAL,
      data_inicio_filtro: data_inicial,
      data_fim_filtro: data_final,
      competencia_inicial: competenciaInicial,
      competencia_final: competenciaFinal,
      codigo_lote: lote || null,
      mensagem: "Iniciando atualização...",
    });

    try {
      const resultadoSincronizacao = await solicitarAtualizacaoGeral({
        dataInicio: data_inicial,
        dataFim: data_final,
        lote,
      });
      const execucao = registrarResultado(
        resultadoSincronizacao,
        execucaoOtimista,
      );
      const temPendencias =
        Number(execucao?.total_pendente_origem) > 0 ||
        Number(execucao?.total_preservado) > 0 ||
        execucao?.situacao === "COM_PENDENCIAS";
      const nenhumLoteEncontrado =
        execucao?.total_encontrado === 0 &&
        Number(execucao?.total_pendente_origem) === 0;

      enqueueSnackbar(
        execucao?.mensagem ??
          (nenhumLoteEncontrado
            ? "Nenhum lote foi encontrado para a competência informada."
            : "Tabela de faturamento atualizada com sucesso."),
        {
          variant: temPendencias
            ? "warning"
            : nenhumLoteEncontrado
              ? "info"
              : "success",
        },
      );
    } catch (erro) {
      const statusHttp = erro?.response?.status;

      if (statusHttp === 409 || statusHttp === 400) {
        cancelarInicio(execucaoOtimista);
        enqueueSnackbar(
          obterMensagemErro(
            erro,
            statusHttp === 409
              ? "Já existe uma atualização geral em andamento."
              : "Revise a competência e os filtros informados.",
          ),
          { variant: "warning" },
        );
        return;
      }

      registrarFalha(erro, execucaoOtimista);
      enqueueSnackbar(
        obterMensagemErro(
          erro,
          "Não foi possível atualizar a tabela de faturamento. Por favor, tente novamente.",
        ),
        { variant: "error" },
      );
    }
  }

  if (faturamento && visualizacao === "lotes") {
    return (
      <FiltrosLotes
        dataInicio={dataInicio}
        setDataInicio={setDataInicio}
        dataFim={dataFim}
        setDataFim={setDataFim}
        competencia={competencia}
        setCompetencia={setCompetencia}
        competenciaPadrao={competenciaPadrao}
        competenciaInicial={competenciaInicial}
        setCompetenciaInicial={setCompetenciaInicial}
        competenciaFinal={competenciaFinal}
        setCompetenciaFinal={setCompetenciaFinal}
        protocolo={protocoloFiltroAtual}
        setProtocolo={setProtocoloFiltro}
        plano={planoFiltroAtual}
        setPlano={setPlanoFiltro}
        listaConvenios={listaConvenios}
        responsavelPlano={responsavelPlanoFiltroAtual}
        setResponsavelPlano={setResponsavelPlanoFiltro}
        listaResponsaveisFiltrados={listaResponsaveisFiltrados}
        lote={loteFiltroAtual}
        setLote={setLoteFiltro}
        situacao={situacaoFiltroAtual}
        setSituacao={setSituacaoFiltro}
        setAtualizaTabelaFaturamento={setAtualizaTabelaFaturamento}
        setAtualizaGeradoSoftlab={setAtualizaGeradoSoftlab}
        atualizacaoGeralAtiva={atualizacaoGeralAtiva}
        carregandoTabela={carregandoTabela}
        onSincronizar={sincronizaTabelasFaturamento}
      />
    );
  }

  return (
    <>
      <Paper id="container-formulario-lotes-faturamento">
        <GridContainer
          spacing={1}
          justifyContent="space-evenly"
          alignItems="center"
        >
          <GridItem xs={6}>
            <GridContainer
              direction="row"
              justifyContent="space-evenly"
              alignItems="center"
              spacing={1}
              sx={{
                border: "0.1rem solid #CDCDCD",
                borderRadius: "0.2rem",
                padding: "1rem",
              }}
            >
              <GridItem xs={3}>
                <DataPickerAtualizado
                  data={dataInicio}
                  labelName="Data inicial"
                  setData={setDataInicio}
                />
              </GridItem>
              <GridItem xs={3}>
                <DataPickerAtualizado
                  data={dataFim}
                  labelName="Data final"
                  setData={setDataFim}
                />
              </GridItem>
              <GridItem xs={2}>
                <TextField
                  id="competencia"
                  label="Competência"
                  placeholder="mm/aaaa"
                  value={competencia}
                  style={{ marginRight: "1rem", width: "100%" }}
                  onChange={(event) => {
                    const comp = event.target.value;
                    if (comp.length === 7) {
                      setCompetencia(event.target.value);
                    } else if (comp.length === 0) {
                      setCompetencia("");
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    inputComponent: MascaraDeCompetencia,
                  }}
                />
              </GridItem>
              <GridItem xs={3}>
                <TextField
                  placeholder="Nota Fiscal"
                  value={notaFiscal}
                  onChange={(e) => setNotaFiscal(e.target.value)}
                />
              </GridItem>
              <GridItem xs={1}>
                <IconButton
                  style={{
                    margin: "auto",
                    height: "3rem",
                    background: "#003399",
                  }}
                  onClick={() => {
                    if (recebimento) {
                      setAtualizaTabela((prevState) => !prevState);
                    } else {
                      setAtualizaTabelaFaturamento((prevState) => !prevState);
                      setAtualizaGeradoSoftlab((prevState) => !prevState);
                    }
                  }}
                  aria-label={
                    faturamento ? "Pesquisar faturamento" : "Pesquisar"
                  }
                >
                  <Search style={{ color: "white" }} />
                </IconButton>
              </GridItem>
            </GridContainer>
          </GridItem>
          <GridItem xs={6}>
            {/* segundo Formulario */}
            <GridContainer
              justifyContent="space-evenly"
              alignItems="center"
              spacing={1}
            >
              <GridItem xs={2}>
                <FormControl fullWidth>
                  <InputLabel id="protocolo-selecionado-label">
                    Protocolo
                  </InputLabel>
                  <Input
                    endAdornment={
                      <InputAdornment position="end">
                        <Search style={{ color: "rgba(0,0,0,0.6)" }} />
                      </InputAdornment>
                    }
                    placeholder="Protocolo"
                    value={protocolo}
                    onChange={(e) =>
                      debounce(setProtocolo(e.target.value.toUpperCase()), 500)
                    }
                    id="search-prot"
                  />
                </FormControl>
              </GridItem>
              <GridItem xs={3}>
                <AutocompleteMultiple
                  opcoes={listaResponsaveisFiltrados}
                  setValue={setResponsavelPlano}
                  value={responsavelPlano}
                  optionLabel="grupo"
                  label="Grupos"
                />
              </GridItem>
              <GridItem xs={3}>
                <FormControl fullWidth>
                  <AutocompleteMultiple
                    opcoes={listaConvenios}
                  setValue={setPlano}
                  value={plano}
                  optionLabel="convenio"
                  getOptionLabel={obterLabelConvenio}
                  filterOptions={filtrarConvenios}
                  label="Planos de saúde"
                  />
                </FormControl>
              </GridItem>
              <GridItem xs={2}>
                <FormControl fullWidth>
                  <InputLabel id="lote-selecionado-label">Lote</InputLabel>
                  <Input
                    endAdornment={
                      <InputAdornment position="end">
                        <Search style={{ color: "rgba(0,0,0,0.6)" }} />
                      </InputAdornment>
                    }
                    placeholder="Lote"
                    value={lote}
                    onChange={(e) =>
                      debounce(setLote(e.target.value.toUpperCase()), 500)
                    }
                    id="search-lote"
                  />
                </FormControl>
              </GridItem>
              {faturamento ? (
                <GridItem xs={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Cached />}
                    onClick={sincronizaTabelasFaturamento}
                    disabled={Boolean(atualizacaoGeralAtiva)}
                  >
                    {atualizacaoGeralAtiva
                      ? "Atualização em andamento"
                      : "Atualizar Tabelas"}
                  </Button>
                </GridItem>
              ) : (
                <GridItem xs={2}>
                  <Button
                    variant="contained"
                    onClick={() => setOpenModalInformarFaturamento(true)}
                  >
                    Informar Recebimento
                  </Button>
                </GridItem>
              )}
            </GridContainer>
          </GridItem>
        </GridContainer>
      </Paper>
    </>
  );
}
